import { WarningOctagon } from '@phosphor-icons/react';
import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';

import { RequestIdBadge } from '@/components/ui/States';
import { i18n } from '@/lib/i18n';
import { captureError, errorIdFor } from '@/lib/observability';

/**
 * Ekran pada aplikacije: „Nešto nije u redu“, šifra greške (request ID ili novi ID koji je i tag u Sentryju)
 * i ponovno učitavanje. Koristi `i18n.t` direktno jer može da se prikaže i kad je pao provider.
 */
export function CrashScreen({ errorId }: { errorId: string }) {
  const t = i18n.t.bind(i18n);
  const [copied, setCopied] = useState(false);
  return (
    <section
      role="alert"
      data-testid="crash-screen"
      className="mx-auto my-16 flex max-w-[560px] flex-col items-start gap-[18px] rounded-xl border border-line bg-surface p-9"
    >
      <span className="grid h-[52px] w-[52px] place-items-center rounded-xl bg-danger-bg text-danger">
        <WarningOctagon size={26} />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="m-0 font-heading text-2xl leading-[1.2] font-semibold tracking-[-.015em]">
          {t('common:support.crashTitle')}
        </h1>
        <p className="m-0 text-[15px] leading-[1.6] text-muted">{t('common:support.crashBody')}</p>
      </div>
      <div className="flex flex-col gap-1">
        <RequestIdBadge requestId={errorId} onCopied={() => setCopied(true)} />
        <span className="text-xs text-muted" aria-live="polite">
          {copied ? t('common:support.codeCopied') : t('common:support.codeHint')}
        </span>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex min-h-[42px] items-center rounded-md bg-btn px-[18px] text-sm font-semibold text-btn-fg hover:bg-btn-h"
      >
        {t('common:support.reload')}
      </button>
    </section>
  );
}

/** Greška rute (TanStack Router `defaultErrorComponent`): šalje se u Sentry jednom i prikazuje CrashScreen. */
export function RouteErrorScreen({ error }: { error: unknown }) {
  const [errorId] = useState(() => errorIdFor(error));
  useEffect(() => {
    captureError(error, { source: 'router', errorId });
  }, [error, errorId]);
  return <CrashScreen errorId={errorId} />;
}

interface State {
  errorId: string | null;
}

/** Granica greške oko cijele aplikacije (main.tsx). */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { errorId: null };

  static getDerivedStateFromError(error: unknown): State {
    return { errorId: errorIdFor(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    captureError(error, {
      source: 'boundary',
      errorId: this.state.errorId ?? undefined,
      ...(info.componentStack ? { componentStack: info.componentStack } : {}),
    });
  }

  override render(): ReactNode {
    if (this.state.errorId) return <CrashScreen errorId={this.state.errorId} />;
    return this.props.children;
  }
}
